
import { Vue, Component, Prop } from "vue-property-decorator";
import moment from "moment";
import getContractorsMultiselectConditions from '@/helpers/getContractorsMultiselectConditions';

import GET_CONTRACTOR_BY_PROJECT_ID from "@/apollo/contractors/GetContractorByProjectId.gql";
import CREATE_PROJECT_ACTIVITY from "@/apollo/projectActivities/CreateActivity.gql";
import UPDATE_PROJECT_ACTIVITY from "@/apollo/projectActivities/UpdateActivity.gql";
import UPDATE_PROJECT_ACTIVITY_RELATED_DATA from "@/apollo/projectActivities/UpdateProjectActivityRelatedData.gql";
import GET_PROJECT_WITH_DATES from "@/apollo/projects/GetProjectWithDates.gql";

import ModalFullScreen from "@/components/modal/ModalFullScreen.vue";
import DropdownFilter from "@/components/filters/DropdownFilter.vue";
import FormDatepicker from "@/components/form/FormDatepicker.vue";
import UiButton from "@/components/button/ui-button.vue";
import FormTextarea from "@/components/form/FormTextarea.vue";
import FormGroup from "@/components/form/FormGroup.vue";
import FormInputGroup from "@/components/form/FormInputGroup.vue";
import ContractorsMultiselect from "@/components/autocompleteMultiselects/ContractorsMultiselect.vue";
import ProjectsMultiselect from "@/components/autocompleteMultiselects/ProjectsMultiselect.vue";

import ActivityDataForModal from "@/entities/modals/ActivityDataForModal";
import { Contractor, Project } from "@/interface";
import { Project_Activity } from "@/generated/graphql";
import { NOTIFICATION } from "@/entities/Events";
import Message from "@/data/notificationsMessages";
import DisabledDates from "@/entities/DisabledDates";
import ModalContent from "@/components/modal/ModalContent.vue";
import convertSecondsToReadable from "@/utils/convertSecondsToReadable";

interface NewActivity {
  title: string;
  startDate: string | null;
  endDate: string | null;
  budget: string;
  note: string;
  selectedContractorsIds: string[];
  confirmedHours: number;
}

interface ContractorListItem {
  id: string;
  name: string;
}

enum ValidationMsg {
  PROJECT_START = "the project start date",
  PROJECT_END = "the project end date",
}

@Component({
  components: {
    ModalContent,
    ModalFullScreen,
    DropdownFilter,
    FormDatepicker,
    UiButton,
    FormTextarea,
    FormGroup,
    FormInputGroup,
    ContractorsMultiselect,
    ProjectsMultiselect,
  },
  apollo: {
    contractors: {
      query: GET_CONTRACTOR_BY_PROJECT_ID,
      variables() {
        return {
          projectId: this.currentProject?.id,
        };
      },
      update: (data) => data.contractor,
      skip() {
        return this.withContractor;
      },
    },
    chosenProject: {
      query: GET_PROJECT_WITH_DATES,
      variables() {
        const [id] = this.chosenProjectId;

        return {
          id,
        };
      },
      skip() {
        return this.shouldFetchProject;
      },
      update: (data) => data.project_by_pk,
    },
  },
})
export default class ActivityFormModal extends Vue {
  @Prop({ type: Object, default: undefined }) readonly project?: Project;
  @Prop({ type: Object, default: null }) readonly projectActivity!: ActivityDataForModal | null;
  @Prop({ type: String, default: undefined }) readonly contractorId?: string;
  @Prop({ type: String, required: true }) readonly currentUserRole!: string;
  @Prop({ type: String, required: true }) readonly companyId!: string;
  @Prop({ type: Boolean, default: false }) readonly isEdittingLocked!: boolean;

  chosenProjectId: string[] = [];

  contractors: Contractor[] = [];

  chosenProject?: Project = undefined;

  convertSecondsToReadable = convertSecondsToReadable;

  activity: NewActivity = {
    title: "",
    startDate: null,
    endDate: null,
    budget: "",
    note: "",
    confirmedHours: 0,
    selectedContractorsIds: [],
  };

  created(): void {
    if (this.projectActivity) {
      const { confirmed_hours, requested_hours } = this.projectActivity;

      const selectedContractorsIds = this.projectActivity.contractor_id
        ? [this.projectActivity.contractor_id]
        : [];

      this.activity = {
        title: this.projectActivity.title || "",
        startDate: this.projectActivity.start_date || null,
        endDate: this.projectActivity.end_date || null,
        note: this.projectActivity.note || "",
        budget: this.projectActivity.budget || "",
        confirmedHours: (confirmed_hours || 0) || (requested_hours || 0),
        selectedContractorsIds,
      };
    }

    this.chosenProjectId = this.project
      ? [this.project.id!]
      : [];
  }

  get getConfirmedHoursInHours(): number {
    return Number(this.activity.confirmedHours) / 3600;
  }

  get modalTitle(): string {
    return `${this.modalPurpose} Task`;
  }

  get currentProject(): Project | undefined {
    return this.withContractor
      ? this.chosenProject
      : this.project;
  }

  get possibleStartDate(): Date | undefined {
    if (!this.currentProject) {
      return undefined;
    }
    return new Date(this.currentProject.start_date);
  }

  get possibleEndDate(): Date | undefined {
    if (!this.possibleStartDate) {
      return undefined;
    }
    return moment(this.possibleStartDate).add(1, 'd').toDate();
  }

  get shouldFetchProject(): boolean {
    const isProjectChosen = Boolean(this.chosenProjectId.length);
    return !this.withContractor || !isProjectChosen;
  }

  get withContractor(): boolean {
    return Boolean(this.contractorId);
  }

  get activityDisabledDates(): DisabledDates {
    return {
      to: new Date(this.currentProject?.start_date),
      from: moment(this.currentProject?.end_date).add(1, 'd').toDate(),
    };
  }

  get modalPurpose(): string {
    return this.projectActivity?.id ? "Edit" : "Create";
  }

  get buttonText(): string {
    return !this.projectActivity?.isNew
      ? "Update"
      : "Create";
  }

  closeModal(): void {
    this.$root.$emit("projectsChanged");
    this.$emit("close");
  }

  get startDateValidationRules(): string | object {
    if (!this.currentProject) {
      return {
        project_required: [this.currentProject],
      };
    }

    const { start_date, end_date } = this.currentProject;
    const noBeforeProjectStart = `noBefore:${start_date},${ValidationMsg.PROJECT_START}`;
    const noAfterProjectEnd = `noAfter:${end_date},${ValidationMsg.PROJECT_END}`;
    const noAfterEndDate = this.activity.endDate ? "noAfter:@end_date" : "";
    return `${noBeforeProjectStart}|${noAfterEndDate}|${noAfterProjectEnd}|required_field`;
  }

  get endDateValidationRules(): string | object {
    if (!this.currentProject) {
      return {
        project_required: [this.currentProject],
      };
    }

    const { start_date, end_date } = this.currentProject;
    const noBeforeProjectStart = `noBefore:${start_date},${ValidationMsg.PROJECT_START}`;
    const noAfterProjectEnd = `noAfter:${end_date},${ValidationMsg.PROJECT_END}`;
    const noBeforeStartDate = this.activity.startDate ? "noBefore:@start_date" : "";
    return `${noBeforeProjectStart}|${noBeforeStartDate}|${noAfterProjectEnd}|required_field`;
  }

  get contractorsNames(): ContractorListItem[] {
    return this.contractors.map(({ user, id }: Contractor) => ({
      id,
      name: `${user.given_name} ${user.family_name}`,
    }));
  }

  get contractorsConditions(): Object {
    return getContractorsMultiselectConditions(this.currentUserRole);
  }

  get projectsConditions(): Object[] {
    return [
      {
        company_id: {
          _eq: this.companyId,
        },
      },
      {
        completed: {
          _eq: false,
        },
      },
    ];
  }


  async submit(): Promise<void> {
    if (!this.currentProject) {
      return;
    }

    const confirmedHoursInSeconds = this.activity.confirmedHours * 3600;

    const { id: project_id } = this.currentProject;
    const {
      selectedContractorsIds,
      title,
      note,
      budget,
      startDate: start_date,
      endDate: end_date,
      confirmedHours: confirmed_hours,
    } = this.activity;

    const contractor_id = this.withContractor
      ? this.contractorId
      : selectedContractorsIds[0] ?? null;


    const activity = {
      contractor_id,
      project_id,
      start_date,
      end_date,
      title,
      note,
      budget: Number(budget),
      confirmed_hours: confirmedHoursInSeconds, // Save in seconds
    };

    
    const updateActivity = {
      contractor_id,
      project_id,
      start_date,
      end_date,
      title,
      note,
      budget: Number(budget)
    };

    if (this.projectActivity?.isNew || !this.projectActivity) {
      await this.addActivity(activity);
      this.closeModal();
      return;
    }

    const { id } = this.projectActivity!;
    const updatedActivityData = { id, ...updateActivity };
    await this.updateActivityData(updatedActivityData);
    this.closeModal();
  }


  // async submit(): Promise<void> {
  //   if (!this.currentProject) {
  //     return;
  //   }

  //   const { id: project_id } = this.currentProject;
  //   const {
  //     selectedContractorsIds,
  //     title,
  //     note,
  //     budget,
  //     startDate: start_date,
  //     endDate: end_date,
  //     confirmedHours: confirmed_hours,
  //   } = this.activity;

  //   const contractor_id = this.withContractor
  //     ? this.contractorId
  //     : selectedContractorsIds[0] ?? null;

  //   const activity = {
  //     contractor_id,
  //     project_id,
  //     start_date,
  //     end_date,
  //     title,
  //     note,
  //     budget: Number(budget),
  //     confirmed_hours,
  //   };

  //   if (this.projectActivity?.isNew || !this.projectActivity) {
  //     await this.addActivity(activity);
  //     this.closeModal();
  //     return;
  //   }

  //   const { id } = this.projectActivity!;
  //   const updatedActivityData = { id, ...activity };
  //   await this.updateActivityData(updatedActivityData);
  //   this.closeModal();
  // }

  async addActivity(activity: Partial<Project_Activity>): Promise<void> {
    try {
      await this.createActivity(activity);
      this.$emit("activityAdded", activity);
      this.$eventBus.emit(NOTIFICATION, Message.successMessage("Visit was created successfully."));
    } catch (e) {
      this.$eventBus.emit(NOTIFICATION, Message.errorMessage(e.message));
    }
  }

  async updateActivityData(activity: Partial<Project_Activity>): Promise<void> {
    try {
      const { id, ...activityData } = activity;
      const customRequestId = this.projectActivity?.custom_requests?.[0]?.id;
      const initialContractorSelected = !Boolean(this.projectActivity?.contractor_id);
      const contractorSelected = Boolean(activity.contractor_id);

      if (customRequestId && contractorSelected && initialContractorSelected) {
        await this.updateProjectActivityAndRelatedData(activity, customRequestId);
      } else {
        await this.updateBasicActivityData(id, activityData);
      }

      this.$emit("activityUpdated", activity);
      this.$eventBus.emit(NOTIFICATION, Message.successMessage("Task was updated successfully."));
    } catch (e) {
      this.$eventBus.emit(NOTIFICATION, Message.errorMessage(e.message));
    }
  }

  private createActivity(activity: Partial<Project_Activity>) {
    return this.$apollo.mutate({
      mutation: CREATE_PROJECT_ACTIVITY,
      variables: {
        object: {
          ...activity,
        },
      },
    });
  }

  private updateProjectActivityAndRelatedData(activity: Partial<Project_Activity>, customRequestId: string) {
    const { id, ...setActivity } = activity;
    return this.$apollo.mutate({
      mutation: UPDATE_PROJECT_ACTIVITY_RELATED_DATA,
      variables: {
        id,
        set: setActivity,
        customRequestId
      },
    });
  }

  private updateBasicActivityData(id: string, activity: Partial<Project_Activity>) {
    return this.$apollo.mutate({
      mutation: UPDATE_PROJECT_ACTIVITY,
      variables: {
        id,
        set: activity,
      },
    });
  }
}
